import { ProgressBar, Tag } from 'antd-mobile';
import ReactECharts from 'echarts-for-react';
import { formatThousandAndUnit, keepOnePercentOrLine } from '@/utils/utils';
import { EnergyCostResponse, TimeResolution } from '@/api/energyOverview';
import styles from './index.module.scss';
import electricImg from '../../images/icon-electric@2x.png';
import waterImg from '../../images/icon-water@2x.png';
import gasImg from '../../images/icon-gas@2x.png';
import { colors, getElectricityChartOption } from './options';

interface EnergyCostProps {
  energyCostData?: EnergyCostResponse;
  dimension: TimeResolution;
}

const EnergyCost = ({ energyCostData = {} }: EnergyCostProps) => {
  // 安全访问数据
  const { electricityData, waterData, gasData, plan, actual, ratio } = energyCostData;

  return (
    <div className={styles.costSection}>
      {/* 能源成本概览 */}
      <div className={styles.costPanel}>
        <div className={styles.sectionTitle}>能源成本</div>
        <div className={styles.costSummary}>
          {/* 左侧：计划成本和实际成本 */}
          <div className={styles.costLeft}>
            <div className={styles.costRow}>
              <span className={styles.costLabel}>计划成本：</span>
              <span className={styles.costValue}>
                <span className={styles.unit}>{formatThousandAndUnit(plan).value}</span>
                <span>{formatThousandAndUnit(plan).unit}元</span>
              </span>
            </div>
            <div className={styles.costRow}>
              <span className={styles.costLabel}>实际成本：</span>
              <span className={styles.costValue}>
                <span className={styles.unit}>{formatThousandAndUnit(actual).value}</span>
                <span>{formatThousandAndUnit(actual).unit}元</span>
              </span>
            </div>
          </div>
          {/* 右侧：占比和进度条 */}
          <div className={styles.costRight}>
            <div className={styles.progressRow}>
              <ProgressBar
                percent={Math.min(100, Math.abs(ratio || 0) * 100)}
                style={{
                  margin: '8px 0',
                  '--fill-color': 'linear-gradient(to right, #CDFFF8, #A0E4FF)',
                  '--track-color': 'rgba(255,255,255,0.2)',
                }}
              />
            </div>
            <div className={styles.percentRow}>
              <span>占比(实际/计划)：</span>
              <span className={styles.percentValue}>{keepOnePercentOrLine(ratio)}%</span>
            </div>
          </div>
        </div>
      </div>
      {/* 用电、用水、用气成本三小块 */}
      <div className={styles.costDetail}>
        <div className={styles.costItem}>
          <div className={styles.itemName}>
            用电成本
            <Tag style={{ '--background-color': '#DFFADA', '--text-color': '#00B578', '--border-color': '#DFFADA' }}>
              {keepOnePercentOrLine(electricityData?.ratio)}%
            </Tag>
          </div>
          <span className={styles.itemCost}>
            <span>{formatThousandAndUnit(electricityData?.cost).value}</span>
            <span>{formatThousandAndUnit(electricityData?.cost).unit}元</span>
          </span>
        </div>
        <div className={styles.costItem}>
          <div className={styles.itemName}>
            用水成本
            <Tag style={{ '--background-color': '#E3EFFF', '--text-color': '#1677FF', '--border-color': '#E3EFFF' }}>
              {keepOnePercentOrLine(waterData?.ratio)}%
            </Tag>
          </div>
          <span className={styles.itemCost}>
            <span>{formatThousandAndUnit(waterData?.cost).value}</span>
            <span>{formatThousandAndUnit(waterData?.cost).unit}元</span>
          </span>
        </div>
        <div className={styles.costItem}>
          <div className={styles.itemName}>
            用气成本
            <Tag style={{ '--background-color': '#FFF0DB', '--text-color': '#FF9F18', '--border-color': '#FFF0DB' }}>
              {keepOnePercentOrLine(gasData?.ratio)}%
            </Tag>
          </div>
          <span className={styles.itemCost}>
            <span>{formatThousandAndUnit(gasData?.cost).value}</span>
            <span>{formatThousandAndUnit(gasData?.cost).unit}元</span>
          </span>
        </div>
      </div>

      {/* 用电成本模块 */}
      <div className={styles.costModule}>
        <div className={styles.moduleHeader}>
          <div className={styles.moduleTitle}>
            <img src={electricImg} width={16} alt="" />
            用电成本
          </div>
          <Tag style={{ '--background-color': '#fff', '--text-color': '#00B578', '--border-color': '#fff' }}>
            {keepOnePercentOrLine(electricityData?.ratio)}%
          </Tag>
        </div>
        <div className={styles.moduleContent}>
          {/* 左侧饼状图 */}
          <div className={styles.chartArea}>
            <ReactECharts option={getElectricityChartOption(electricityData!)} style={{ height: 100, width: '100%' }} />
          </div>
          {/* 右侧电费详情 */}
          <div className={styles.detailArea}>
            <div className={styles.totalRow}>
              <span>用电成本-电度电费</span>
              <span className={styles.totalValue}>
                {formatThousandAndUnit(electricityData?.cost).value}
                {formatThousandAndUnit(electricityData?.cost).unit}元
              </span>
            </div>
            <div className={styles.periodList}>
              <div className={styles.periodItem}>
                <div className={styles.periodName}>
                  <span className={styles.dot} style={{ background: colors[0] }} />
                  尖电费
                </div>
                <span className={styles.periodValue}>
                  {formatThousandAndUnit(electricityData?.summit?.value).value}
                  {formatThousandAndUnit(electricityData?.summit?.value).unit}元
                </span>
                <span className={styles.periodRatio}>{keepOnePercentOrLine(electricityData?.summit?.ratio)}%</span>
              </div>
              <div className={styles.periodItem}>
                <div className={styles.periodName}>
                  <span className={styles.dot} style={{ background: colors[1] }} />
                  峰电费
                </div>
                <span className={styles.periodValue}>
                  {formatThousandAndUnit(electricityData?.peak?.value).value}
                  {formatThousandAndUnit(electricityData?.peak?.value).unit}元
                </span>
                <span className={styles.periodRatio}>{keepOnePercentOrLine(electricityData?.peak?.ratio)}%</span>
              </div>
              <div className={styles.periodItem}>
                <div className={styles.periodName}>
                  <span className={styles.dot} style={{ background: colors[2] }} />
                  平电费
                </div>
                <span className={styles.periodValue}>
                  {formatThousandAndUnit(electricityData?.plain?.value).value}
                  {formatThousandAndUnit(electricityData?.plain?.value).unit}元
                </span>
                <span className={styles.periodRatio}>{keepOnePercentOrLine(electricityData?.plain?.ratio)}%</span>
              </div>
              <div className={styles.periodItem}>
                <div className={styles.periodName}>
                  <span className={styles.dot} style={{ background: colors[3] }} />
                  谷电费
                </div>
                <span className={styles.periodValue}>
                  {formatThousandAndUnit(electricityData?.valley?.value).value}
                  {formatThousandAndUnit(electricityData?.valley?.value).unit}元
                </span>
                <span className={styles.periodRatio}>{keepOnePercentOrLine(electricityData?.valley?.ratio)}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 用水成本模块 */}
      <div className={styles.costModule}>
        <div className={styles.moduleHeader}>
          <div className={styles.moduleTitle}>
            <img src={waterImg} width={16} alt="" />
            用水成本
          </div>
          <Tag style={{ '--background-color': '#fff', '--text-color': '#1677FF', '--border-color': '#fff' }}>
            {keepOnePercentOrLine(waterData?.ratio)}%
          </Tag>
        </div>
        <div className={styles.waterContent}>
          <div className={`${styles.item} ${styles.noBorderBottom}`}>
            <div>
              自来水成本({formatThousandAndUnit(waterData?.freshWater?.actual).unit}
              元)
            </div>
            <div className={styles.value}>
              <span>{formatThousandAndUnit(waterData?.freshWater?.actual).value}</span>
              <span className={styles.ratio}>{keepOnePercentOrLine(waterData?.freshWater?.ratio)}%</span>
            </div>
          </div>
          <div className={`${styles.item} ${styles.noBorderBottom}`}>
            <div>
              中水成本(
              {formatThousandAndUnit(waterData?.middleWater?.actual).unit}元)
            </div>
            <div className={styles.value}>
              <span>{formatThousandAndUnit(waterData?.middleWater?.actual).value}</span>
              <span className={styles.ratio}>{keepOnePercentOrLine(waterData?.middleWater?.ratio)}%</span>
            </div>
          </div>
        </div>
      </div>
      {/* 用气成本模块 */}
      <div className={styles.costModule}>
        <div className={styles.moduleHeader}>
          <div className={styles.moduleTitle}>
            <img src={gasImg} width={16} alt="" />
            用气成本
          </div>
          <Tag style={{ '--background-color': '#fff', '--text-color': '#FF9F18', '--border-color': '#fff' }}>
            {keepOnePercentOrLine(gasData?.ratio)}%
          </Tag>
        </div>
        <div className={styles.gasContent}>
          {gasData?.gasEnergyMediumData?.map((item, index) => {
            const totalItems = gasData?.gasEnergyMediumData?.length || 0;
            // 数量<=3不需要边框，数量>=4时前3个需要边框，后面的不需要
            const needBorderBottom = totalItems >= 4 && index < 3;
            return (
              <div
                key={item.energyMediumId}
                className={`${styles.item} ${!needBorderBottom ? styles.noBorderBottom : ''}`}
              >
                <span>
                  {item.energyMediumName}({formatThousandAndUnit(item?.actual).unit}元)
                </span>
                <div className={styles.value}>
                  <span>{formatThousandAndUnit(item.actual).value}</span>
                  <span className={styles.ratio}>{keepOnePercentOrLine(item?.ratio)}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EnergyCost;
